"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
	useState,
} from "react";
import type {
	DeclareInput,
	LookupInput,
	WithdrawalLine,
	WithdrawalLookupResult,
} from "../schema";

export interface WithdrawalActions {
	lookupOrder: (input: LookupInput) => Promise<WithdrawalLookupResult>;
	declareWithdrawal: (
		input: DeclareInput,
	) => Promise<{ success: true; submittedAt: string }>;
}

export type WithdrawalPhase = "idle" | "not_found" | "expired" | "error";

interface FoundOrder {
	orderNumber: number;
	items: WithdrawalLine[];
}

export interface WithdrawalContextValue {
	email: string;
	setEmail: (value: string) => void;
	orderNumber: string;
	setOrderNumber: (value: string) => void;
	phase: WithdrawalPhase;
	isLookingUp: boolean;
	isSubmitting: boolean;
	declared: boolean;
	order: FoundOrder | null;
	getQuantity: (lineId: number) => number;
	setQuantity: (lineId: number, quantity: number) => void;
	selectedItems: WithdrawalLine[];
	lookup: () => Promise<void>;
	confirm: () => Promise<void>;
	reset: () => void;
	remaining: (lineId: number) => number;
}

const WithdrawalContext = createContext<WithdrawalContextValue | null>(null);

export const useWithdrawal = () => {
	const context = useContext(WithdrawalContext);
	if (!context) {
		throw new Error("Withdrawal.* must be used within <Withdrawal.Root>");
	}
	return context;
};

export interface WithdrawalRootProps {
	actions: WithdrawalActions;
	locale: string;
	onDeclared?: (result: { submittedAt: string }) => void;
	children: ReactNode;
}

export const WithdrawalRoot = ({
	actions,
	locale,
	onDeclared,
	children,
}: WithdrawalRootProps) => {
	const [email, setEmail] = useState("");
	const [orderNumber, setOrderNumber] = useState("");
	const [phase, setPhase] = useState<WithdrawalPhase>("idle");
	const [isLookingUp, setIsLookingUp] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [declared, setDeclared] = useState(false);
	const [order, setOrder] = useState<FoundOrder | null>(null);
	const [quantities, setQuantities] = useState<Record<number, number>>({});

	const lookup = async () => {
		setIsLookingUp(true);
		setPhase("idle");
		try {
			const result = await actions.lookupOrder({ email, orderNumber });
			if (result.status === "ok") {
				setOrder({ orderNumber: result.orderNumber, items: result.items });
				setQuantities({});
			} else {
				setOrder(null);
				setPhase(result.status);
			}
		} catch {
			setPhase("error");
		} finally {
			setIsLookingUp(false);
		}
	};

	const getQuantity = (lineId: number) => quantities[lineId] ?? 0;

	const remainingOf = (lineId: number) => {
		const line = order?.items.find((i) => i.id === lineId);
		return line ? line.quantity - (line.claimed ?? 0) : 0;
	};

	const setQuantity = (lineId: number, quantity: number) => {
		const remaining = remainingOf(lineId);
		setQuantities((prev) => ({
			...prev,
			[lineId]: Math.max(0, Math.min(quantity, remaining)),
		}));
	};

	const selectedItems = useMemo<WithdrawalLine[]>(() => {
		if (!order) return [];
		return order.items
			.map((line) => ({ ...line, quantity: quantities[line.id] ?? 0 }))
			.filter((line) => line.quantity > 0);
	}, [order, quantities]);

	const confirm = async () => {
		if (!order || selectedItems.length === 0) return;
		setIsSubmitting(true);
		try {
			const result = await actions.declareWithdrawal({
				email,
				orderNumber: order.orderNumber,
				locale,
				items: selectedItems,
			});
			setDeclared(true);
			onDeclared?.({ submittedAt: result.submittedAt });
		} catch {
			setPhase("error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const reset = () => {
		setOrder(null);
		setQuantities({});
		setPhase("idle");
		setDeclared(false);
	};

	const value: WithdrawalContextValue = {
		email,
		setEmail,
		orderNumber,
		setOrderNumber,
		phase,
		isLookingUp,
		isSubmitting,
		declared,
		order,
		getQuantity,
		setQuantity,
		selectedItems,
		lookup,
		confirm,
		reset,
		remaining: remainingOf,
	};

	return (
		<WithdrawalContext.Provider value={value}>
			{children}
		</WithdrawalContext.Provider>
	);
};
